import {
  USER_LOGIN_API,
  USER_SIGNUP_URL,
  REGISTER_WORKSPACE_URL,
  JOIN_EXISTING_WORKSPACE_URL,
  CHANGED_PASSWORD,
  USER_DETAIL,
  JOIN_INVITED_USER_TO_WORKSPACE_URL,
  USER_PROFILE,
  SWITCH_USER_WORKSPACE,
  LOGOUT,
  SUBSCRIPTION_DETAILS,
} from "lib/constants";
import { getAPICall, postAPICall, putAPICall } from "lib/callApi";
import PropTypes from "prop-types";
import { createContext, useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import UpdateProfile from "pages/Profile-Settings/UpdateProfile";
import { loginUserAction } from "../redux/slice/users";
import { subscriptionActions } from "../redux/slice/subscription";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const storeData = useSelector((state) => state.loginUser.loginUserState);

  const [user, setUser] = useState(storeData);
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [showUpdateProfileModal, setShowUpdateProfileModal] = useState(false);

  const navigate = useNavigate();

  const location = useLocation();

  useEffect(() => {
    if (storeData) {
      setUser({ ...storeData });
    }
  }, [storeData]);

  useEffect(() => {
    if (error) setError(null);
  }, [location.pathname]);

  useEffect(() => {
    if (["/verify-email", "/workspace", "/subscription-plans"].includes(location.pathname)) {
      setShowUpdateProfileModal(false);
      return;
    }
    const initialTimeAtCompany =
      user?.UserCompaniesLinks?.[0]?.FK_user_company_tenure_years_id ||
      user?.UserCompaniesLinks?.FK_user_company_tenure_years_id;
    const initialAverageSalesCycleLength =
      user?.UserCompaniesLinks?.[0]?.FK_user_sales_cycles_id ||
      user?.UserCompaniesLinks?.FK_user_sales_cycles_id;
    if (
      user &&
      !user?.disabled &&
      user?.emailVerified &&
      user?.hasActiveSubscription &&
      user?.hasActiveWorkspaces &&
      (!initialTimeAtCompany || !initialAverageSalesCycleLength)
    ) {
      setShowUpdateProfileModal(true);
    } else {
      setShowUpdateProfileModal(false);
    }
  }, [user, location.pathname]);

  //   login
  const login = async (dataToPass, redirect) => {
    try {
      const result = await postAPICall(USER_LOGIN_API, null, dataToPass);

      // example of how to call upcoming apis using redux-sagaMiddleware , kindly ignore for now.
      // const result = dispatch(loginUserAction.getLoginUsersSlice({ ...dataToPass }));
      setIsLoading(true);

      if (result.data.success) {
        setIsLoading(false);
        const userObj = result.data.data;
        userObj.dateFormat = result.data?.dateFormat;
        if (result.data.data.type === "admin") {
          // navigate("/registrations", { replace: true });

          localStorage.setItem(
            "proxyUserState",
            JSON.stringify({
              token: result.data.token,
              user: userObj,
            })
          );

          navigate("/proxy-login");
        } else {
          dispatch(subscriptionActions.setSubscriptionLoading(true));
          localStorage.setItem("user", JSON.stringify(result.data.data.type));
          localStorage.setItem("token", result.data.token);
          dispatch(loginUserAction.getLoginUserData(userObj));

          if (redirect) {
            navigate(`/workspace${redirect}`, { replace: true });
          } else if (!result.data.data.emailVerified) {
            navigate("/verify-email");
          } else if (result.data.data.disabled) {
            navigate("/workspace");
          } else if (!result.data.data.hasActiveWorkspaces) {
            navigate("/workspace");
          } else if (!result.data.data.hasActiveSubscription) {
            navigate("/subscription-plans");
          } else {
            navigate("/home");
          }
        }
        setUser({ ...result.data.data });
      } else {
        setIsLoading(false);
        setError(result.data.message);
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  // signout
  const handleLogout = () => {
    dispatch(loginUserAction.logoutUsersSlice());
    dispatch(subscriptionActions.clearSubscription());
    // we don't need to await here, just trying to make sure we don't have a bunch of hanging sessions
    putAPICall(LOGOUT, true);
    localStorage.clear();
    navigate("/sign-in");
  };

  const handleWorkspaceSwitch = async (workspaceId) => {
    const result = await putAPICall(`${SWITCH_USER_WORKSPACE}/${workspaceId}`, true);
    if (result.status === 200) {
      navigate("/");
      window.location.reload();
    }
  };

  //  new user signup
  const signUp = async (dataToPass) => {
    try {
      setIsLoading(true);
      const result = await postAPICall(USER_SIGNUP_URL, null, dataToPass);

      if (result.data.success) {
        setUser({ ...result.data.data });
        setIsLoading(false);

        const searchParams = new URLSearchParams();
        searchParams.append("email", dataToPass.email);

        navigate(`/verify-email?${searchParams.toString()}`, { replace: true });
      } else {
        setIsLoading(false);
        setError(result.data.message);
      }
    } catch (err) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  // Register workspace
  const registerWorkspace = async (dataToPass) => {
    try {
      setIsLoading(true);
      const result = await postAPICall(REGISTER_WORKSPACE_URL, null, dataToPass);

      if (result.data.success) {
        // They have workspaces and just created a new one, trigger a manual redirect
        if (
          (user.userCompanyLinkId &&
            user.userCompanyLinkId !== result.data.data.userCompanyLinkId) ||
          result?.data?.data?.skipPasswordCreation
        ) {
          const refreshUserDetail = await getAPICall(USER_DETAIL, true);
          dispatch(loginUserAction.getLoginUserData(refreshUserDetail?.data?.data));
          setUser({ ...refreshUserDetail?.data?.data });

          const subscriptionResult = await getAPICall(SUBSCRIPTION_DETAILS, true);

          if (subscriptionResult.data.success) {
            dispatch(subscriptionActions.setSubscriptionData(subscriptionResult.data.data));
          } else {
            dispatch(subscriptionActions.setSubscriptionError(subscriptionResult.data.message));
          }

          navigate("/subscription-plans", { replace: true });
          setIsLoading(false);
        } else {
          setUser({ ...user, ...result.data.data });
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
        setError(result.data.message);
      }
    } catch (err) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  //  Join existing workspace
  const joinExistingWorkspace = async (dataToPass) => {
    try {
      setIsLoading(true);
      const result = await postAPICall(JOIN_EXISTING_WORKSPACE_URL, null, dataToPass);

      if (result.data.success) {
        setUser({ ...user, ...result.data.data });
        if (!result.data.data.passwordToken) {
          if (window.location.href.includes("/workspace")) window.location.reload();
          else navigate("/workspace", { replace: true });
        }
        setIsLoading(false);
      } else {
        setIsLoading(false);
        setError(result.data.message);
      }
    } catch (err) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  //  join invited user to workspace
  const joinInvitedUserToWorkspace = async (dataToPass) => {
    try {
      setIsLoading(true);
      const result = await postAPICall(JOIN_INVITED_USER_TO_WORKSPACE_URL, null, dataToPass);

      if (result.data.success) {
        const refreshUserDetail = await getAPICall(USER_DETAIL, true);
        dispatch(loginUserAction.getLoginUserData(refreshUserDetail?.data?.data));
        setUser({ ...refreshUserDetail?.data?.data });
        dispatch(subscriptionActions.setSubscriptionLoading(true));
        navigate("/home", { replace: true });
        setIsLoading(false);
      } else {
        setIsLoading(false);
        setError(result.data.message);
      }
    } catch (err) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  // change password when signing up new users
  const changePassword = async (dataToPass, redirectOnSuccess) => {
    try {
      setIsLoading(true);
      const result = await putAPICall(CHANGED_PASSWORD, null, dataToPass);

      if (result.data.success) {
        setIsLoading(false);
        localStorage.setItem("token", result.data.token);
        const refreshUserDetail = await getAPICall(USER_DETAIL, true);
        dispatch(loginUserAction.getLoginUserData(refreshUserDetail?.data?.data));
        setUser({ ...refreshUserDetail?.data?.data });
        let redirectTo = "/subscription-plans";
        if (redirectOnSuccess.startsWith("?redirect=workspace-selection")) {
          redirectTo = "/workspace";
        } else if (redirectOnSuccess.startsWith("?redirect=invited-user")) {
          redirectTo = `/workspace${redirectOnSuccess}`;
        } else if (!refreshUserDetail.data.data.emailVerified) {
          redirectTo = "/verify-email";
        } else if (refreshUserDetail.data.data.disabled) {
          redirectTo = "/workspace";
        } else if (!refreshUserDetail.data.data.hasActiveWorkspaces) {
          redirectTo = "/workspace";
        } else if (!refreshUserDetail.data.data.hasActiveSubscription) {
          redirectTo = "/subscription-plans";
        } else {
          redirectTo = "/home";
        }

        if (redirectTo) {
          navigate(redirectTo, { replace: true });
        }
      } else {
        setIsLoading(false);
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      setIsLoading(false);
      const message = err?.response?.data?.errorData?.message || "User is not being verified";
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: message,
        }),
        {
          variant: "custom",
        }
      );

      if (message === "Token has expired. Please click Forgot Password to change your password!") {
        navigate("/forgot-password");
      } else {
        window.location.reload();
      }
    }
  };

  // Update User Profile
  const updateUserProfile = async (dataToPass) => {
    try {
      setIsLoading(true);

      const result = await await putAPICall(USER_PROFILE, true, dataToPass);

      if (result.data.success) {
        setUser({ ...user, ...result.data.data });
        setIsLoading(false);
      } else {
        setIsLoading(false);
        setError(result.data.message);
      }
      return true;
    } catch (err) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
      return false;
    }
  };

  const handleCloseUpdateProfile = () => {
    setShowUpdateProfileModal(false);
  };

  const loadToken = async (provider, authCode, state) => {
    try {
      const result = await getAPICall(`sso/auth/${provider}?code=${authCode}&state=${state}`, true);

      if (result?.data?.success) {
        localStorage.setItem("user", "user");
        localStorage.setItem("token", result.data.data.userToken);
        const redirectFromSSO = result.data.data.redirect;
        if (redirectFromSSO.includes("workspace")) {
          const refreshUserDetail = await getAPICall(USER_DETAIL, true);
          dispatch(loginUserAction.getLoginUserData(refreshUserDetail?.data?.data));
          setUser({ ...refreshUserDetail?.data?.data });
        }

        navigate(`/${result.data.data.redirect}`, { replace: true });
      } else {
        setIsLoading(false);
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              result?.data?.errorMessage ||
              result?.response?.data?.errorMessage ||
              "Something went wrong",
          }),
          {
            variant: "custom",
          }
        );
        navigate("/sign-in", { replace: true });
      }
    } catch (err) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
      setError(err.message);
    }
  };

  const memoedValue = useMemo(
    () => ({
      user,
      isLoading,
      error,
      login,
      handleLogout,
      changePassword,
      signUp,
      registerWorkspace,
      joinExistingWorkspace,
      joinInvitedUserToWorkspace,
      updateUserProfile,
      handleWorkspaceSwitch,
      loadToken,
    }),
    [user, isLoading, error]
  );

  return (
    <AuthContext.Provider value={memoedValue}>
      {children}
      {showUpdateProfileModal && user ? (
        <UpdateProfile
          open={showUpdateProfileModal}
          handleClose={handleCloseUpdateProfile}
          user={user}
        />
      ) : null}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function useAuth() {
  return useContext(AuthContext);
}
